/**
 * Toolbar の disabled ボタンに hover tooltip で理由を出すための理由キー算出。
 *
 * デザイナー指摘 (2026-08-13): 「調整（最大電力補正）」ボタンは disabled のとき
 * なぜ押せないかが分からない。index.tsx の `canRescale` (共通仕様
 * demand-forecast-common.md AC-DFC-EDIT-001) は以下 4 条件の AND:
 *   isRetailerEditableTarget && currentEditRow !== undefined && !isCompleted && canMutateTargetDate
 * (集計表示で全内訳へ按分して編集できる時〈AC-DFD-054〉は、呼び出し元の forecastGates が
 * isRetailerEditableTarget / hasCurrentEditRow を true として渡す)
 * 複数条件が同時に不成立の場合、最も基礎的なブロック要因を優先して 1 つだけ返す
 * (= 同時に複数理由を出さない。UI 上 tooltip は 1 文言のみ表示するため)。
 *
 * 優先順位: 確定済み > 過去日 > 全コマ GC 済み > 小売未選択
 * (spec `demand-forecast-daily.md` AC-DFD-047b・AC-DFC-EDIT-001。#10963 追加 6・7)。
 * 確定済みは「取り消さない限り変えられない」ため利用者に原因として最も伝わりやすく先頭に置く。
 * 過去日は全コマ GC 済みでもあるため、より具体的な過去日を全コマ GC 済みより優先する。
 * 小売未選択 (BG全体ビュー + 配下複数 で isRetailerEditableTarget=false、または
 * currentEditRow が未解決) は、対象を選び直せば解消しうる最も「解決しやすい」理由なので
 * 最後に置く。
 *
 * 過去日判定は呼び出し元 (index.tsx) で models/canMutate に集約済み。本関数は
 * canMutateTargetDate をそのまま受け取り、false = 過去日 → pastDate に写像する。
 */
export type AdjustmentDisabledReasonKey =
  | 'completed'
  | 'pastDate'
  | 'allGcLocked'
  | 'noRetailer'
  | 'unattributedRemaining'
  /** グループ別の小売の合計タブ。合計は葉の足し算で、グループかその他のタブで入力する。 */
  | 'groupTotal'
  /** グループ別の小売がある BG の BG 全体。BG 全体からは按分できない。 */
  | 'groupForecastBg'

export type AdjustmentDisabledReasonParams = {
  canMutateTargetDate: boolean
  isCompleted: boolean
  /** 全 48 コマが GC 済み (当日断面で gcKoma >= 48)。動かせるコマが無い。 */
  isAllKomaGcLocked: boolean
  isRetailerEditableTarget: boolean
  hasCurrentEditRow: boolean
}

/**
 * canRescale が true になる条件(=有効化条件)を反転して理由キーを返す。
 * 有効 (= 全条件成立) のときは undefined ( = tooltip 不要)。
 */
export function adjustmentDisabledReasonKey(
  params: AdjustmentDisabledReasonParams,
): AdjustmentDisabledReasonKey | undefined {
  const {
    canMutateTargetDate,
    isCompleted,
    isAllKomaGcLocked,
    isRetailerEditableTarget,
    hasCurrentEditRow,
  } = params

  if (isCompleted) return 'completed'
  if (!canMutateTargetDate) return 'pastDate'
  if (isAllKomaGcLocked) return 'allGcLocked'
  if (!isRetailerEditableTarget || !hasCurrentEditRow) return 'noRetailer'
  return undefined
}
